import { mapValues } from 'lodash';
import { isScoredType } from 'js/utils/question-set';
import {
  createOrgAssessmentWeightContext,
  rollupGroupScoresFromChildren,
} from 'js/utils/org-assessment-rollup';

const scoreTeam = (teamNode, questionScores) => {
  const rows = Array.isArray(questionScores) ? questionScores : [];
  const rawTeamScores = rows.find((ts) => {
    return ts.team_id === teamNode.id;
  });

  const rawScoringData = (rawTeamScores?.scores ?? []).reduce(
    (acc, { question_key, question_set_key, question_type, score }) => {
      const questionSetInfo = acc[question_set_key] ?? {
        question_set_key,
        scoredTotal: 0,
        scoredCount: 0,
        total: 0,
        count: 0,
        questions: {},
      };

      const questionInfo = questionSetInfo.questions[question_key] ?? {
        question_key,
        question_type,
        scoredTotal: 0,
        scoredCount: 0,
        total: 0,
        count: 0,
      };

      if (isScoredType(question_type)) {
        questionSetInfo.scoredTotal += score;
        questionSetInfo.scoredCount += 1;
        questionInfo.scoredTotal += score;
        questionInfo.scoredCount += 1;
      }

      questionSetInfo.total += score;
      questionSetInfo.count += 1;
      questionInfo.total += score;
      questionInfo.count += 1;

      questionSetInfo.questions[question_key] = questionInfo;
      acc[question_set_key] = questionSetInfo;

      return acc;
    },
    {}
  );

  const teamScores = mapValues(
    rawScoringData,
    ({
      question_set_key,
      scoredTotal: qsScoredTotal,
      scoredCount: qsScoredCount,
      questions,
    }) => ({
      question_set_key,
      score: qsScoredCount > 0 ? qsScoredTotal / qsScoredCount : null,
      questions: mapValues(
        questions,
        ({
          question_key,
          question_type,
          scoredTotal: qScoredTotal,
          scoredCount: qScoredCount,
          total: qTotal,
          count: qCount,
        }) => ({
          question_key,
          question_type,
          score: qScoredCount > 0 ? qScoredTotal / qScoredCount : null,
          unScore: qCount > 0 ? qTotal / qCount : null,
        })
      ),
    })
  );

  return {
    ...teamNode,
    type: 'team',
    scores: teamScores,
    children: [],
  };
};

function catalogQuestionSets(catalog, template) {
  const byKey = new Map();

  const addSet = (qs) => {
    if (!qs?.key) {
      return;
    }

    if (!byKey.has(qs.key)) {
      byKey.set(qs.key, {
        key: qs.key,
        questions: [...(qs.questions ?? [])],
      });
      return;
    }

    const existing = byKey.get(qs.key);
    const seen = new Set((existing.questions ?? []).map((q) => q.key));
    for (const question of qs.questions ?? []) {
      if (question?.key && !seen.has(question.key)) {
        existing.questions.push(question);
        seen.add(question.key);
      }
    }
  };

  for (const qs of catalog?.question_sets ?? []) {
    addSet(qs);
  }
  for (const qs of template?.assessment?.question_sets ?? []) {
    addSet(qs);
  }

  return [...byKey.values()];
}

export function seedScoresFromQuestionCatalog(scores, catalog, template) {
  if (!scores || Object.keys(scores).length === 0) {
    return scores;
  }

  const next = { ...scores };

  for (const qs of catalogQuestionSets(catalog, template)) {
    const existingSet = next[qs.key] ?? {
      question_set_key: qs.key,
      score: null,
      questions: {},
    };
    const questions = { ...existingSet.questions };

    for (const question of qs.questions ?? []) {
      if (!question?.key || questions[question.key]) {
        continue;
      }

      questions[question.key] = {
        question_key: question.key,
        question_type: question.type ?? 'likert',
        score: null,
        unScore: null,
      };
    }

    next[qs.key] = {
      ...existingSet,
      questions,
    };
  }

  return next;
}

export function seedNodeScoresFromCatalog(node, catalog, template) {
  if (!node) {
    return node;
  }

  return {
    ...node,
    scores: seedScoresFromQuestionCatalog(node.scores, catalog, template),
    children: (node.children ?? []).map((child) =>
      seedNodeScoresFromCatalog(child, catalog, template)
    ),
  };
}

const scoreGroup = (
  groupNode,
  questionScores,
  weightContext,
  pathToNode = []
) => {
  const groupRow = { ...groupNode, type: 'org' };
  const childPath = [...pathToNode, groupRow];

  const children = [
    ...(groupNode.child_orgs ?? []),
    ...(groupNode.teams ?? []),
  ].map((n) =>
    scoreNodeWithContext(n, questionScores, weightContext, childPath)
  );

  const scores = rollupGroupScoresFromChildren(
    children,
    weightContext,
    childPath
  );

  return {
    ...groupNode,
    type: 'org',
    scores,
    children,
  };
};

function isTeamNode(node) {
  return !node.child_orgs && !node.teams;
}

function scoreNodeWithContext(
  node,
  questionScores,
  weightContext,
  pathToNode = []
) {
  return isTeamNode(node)
    ? scoreTeam(node, questionScores)
    : scoreGroup(node, questionScores, weightContext, pathToNode);
}

const scoreNode = (node, questionScores, organization) => {
  const weightContext = createOrgAssessmentWeightContext(organization);
  return scoreNodeWithContext(node, questionScores, weightContext, []);
};

export { scoreNode, scoreNodeWithContext, scoreTeam, scoreGroup };
